import React, { useState, useEffect, useCallback } from 'react';
import { Button, Grid, Alert, Typography, Box } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import { useNavigate } from 'react-router';
import { useLocation } from 'react-router-dom';
import ScaleLoader from 'react-spinners/ScaleLoader';
import noTableData from '../../assets/images/noTableData.svg';

// ✅ Helper to synchronously detect file type
const detectFileType = (urlStr) => {
  if (!urlStr) return 'unsupported';
  try {
    const url = new URL(urlStr);
    const pathname = url.pathname.toLowerCase();
    if (pathname.match(/\.(docx|doc)$/) || url.search.toLowerCase().includes('.docx') || url.search.toLowerCase().includes('.doc')) {
      return 'docx'; // Both .doc and .docx use Office Viewer
    } else if (pathname.match(/\.pdf$/) || url.search.toLowerCase().includes('.pdf')) {
      return 'pdf';
    } else if (pathname.match(/\.(jpeg|jpg|png)$/) || url.search.toLowerCase().includes('.jpeg') || url.search.toLowerCase().includes('.jpg') || url.search.toLowerCase().includes('.png')) {
      return 'image';
    } else {
      return 'unsupported';
    }
  } catch (error) {
    console.error('URL parsing error:', error);
    return 'pdf'; // Default fallback
  }
};

const ViewFile = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const pdfUrl = location.state?.fileUrl;
  const [loading, setLoading] = useState(true);
  const fileType = detectFileType(pdfUrl);
  const [iframeError, setIframeError] = useState(false);
  const [iframeLoadTimeout, setIframeLoadTimeout] = useState(null);

  // ✅ Force loading to false after 30s max (safety)
  useEffect(() => {
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 30000); // 30 second safety limit
    return () => clearTimeout(timeout);
  }, []);

  // ✅ Iframe timeout handler (10s fallback)
  useEffect(() => {
    if (fileType === 'docx' || fileType === 'doc') {
      const timeout = setTimeout(() => {
        setIframeError(true);
      }, 10000); // 10 second timeout
      setIframeLoadTimeout(timeout);
      return () => clearTimeout(timeout);
    }
  }, [fileType]);

  const navigateToPreviousPage = () => {
    const from = location.state?.from;
    const jobCode = location.state?.jobCode;
    if (from) {
      navigate(from, { state: { jobCode }, replace: true });
    } else {
      navigate(-1);
    }
  };

  // ✅ Download handler
  const handleDownload = useCallback(() => {
    if (!pdfUrl) return;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = pdfUrl.split('/').pop() || 'document';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [pdfUrl]);

  // ✅ Microsoft Office Viewer URL
  const getOfficeViewerUrl = (url) => {
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
  };

  // ✅ Unsupported file handler
  if (fileType === 'unsupported') {
    return (
      <div style={{ marginTop: '20px' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={navigateToPreviousPage} sx={{ mb: 2 }}>
          Back
        </Button>
        <Grid style={{ backgroundColor: 'white', marginTop: '20px', padding: '40px' }}>
          <Alert severity="warning" style={{ maxWidth: '600px', margin: '0 auto' }}>
            <Typography variant="h6">File type not supported</Typography>
            <Typography>Only .doc, .docx, .pdf, and image files are supported.</Typography>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              style={{ marginTop: '10px' }}
            >
              Download File
            </Button>
          </Alert>
        </Grid>
      </div>
    );
  }

  return (
    <div style={{ marginTop: '20px' }}>
      <Button startIcon={<ArrowBackIcon />} onClick={navigateToPreviousPage} sx={{ mb: 2 }}>
        Back
      </Button>

      <div style={{ marginTop: '20px' }}>
        {pdfUrl ? (
          <div style={{
            textAlign: 'center',
            fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
            fontSize: '24px',
            fontWeight: '600',
            lineHeight: '23px',
            letterSpacing: '0.01em',
          }}>
            View File
            <div>
              <Grid style={{ backgroundColor: 'white', marginTop: '20px' }}>
                <div style={{ height: 'calc(100vh - 200px)', minHeight: '600px', position: 'relative', width: '100%' }}>

                  {/* ✅ Unified Loading Overlay */}
                  {loading && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      zIndex: 1000
                    }}>
                      <ScaleLoader size={250} color="#0576DC" />
                    </div>
                  )}

                  {fileType === 'pdf' ? (
                    <iframe
                      key={pdfUrl}
                      src={`${pdfUrl}#toolbar=0&navpanes=0&view=FitH`}
                      width="100%"
                      height="100%"
                      style={{ border: 'none' }}
                      title="PDF Viewer"
                      allow="nested-history 'none'"
                      onLoad={() => setLoading(false)}
                    />
                  ) : fileType === 'image' ? (
                    <img
                      src={pdfUrl}
                      alt="Document Viewer"
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                      onLoad={() => setLoading(false)}
                      onError={() => {
                        setIframeError(true);
                        setLoading(false);
                      }}
                    />
                  ) : fileType === 'docx' ? (
                    <>
                      {iframeError ? (
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          justifyContent="center"
                          height="100%"
                          gap={2}
                          p={4}
                        >
                          <Alert
                            severity="error"
                            action={
                              <Button
                                color="inherit"
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownload}
                              >
                                Download
                              </Button>
                            }
                          >
                            Document viewer failed to load.
                            File may be private or temporarily unavailable.
                          </Alert>
                        </Box>
                      ) : (
                        <iframe
                          key={pdfUrl}
                          src={getOfficeViewerUrl(pdfUrl)}
                          width="100%"
                          height="100%"
                          style={{ border: 'none' }}
                          title="Document Viewer"
                          allowFullScreen={true}
                          allow="nested-history 'none'"
                          onLoad={() => {
                            clearTimeout(iframeLoadTimeout);
                            setIframeError(false);
                            setLoading(false);
                          }}
                          onError={() => setIframeError(true)}
                        />
                      )}
                    </>
                  ) : null}
                </div>
              </Grid>
            </div>
          </div>
        ) : (
          <Grid style={{ height: '300px', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', backgroundColor: 'white' }}>
            <img src={noTableData} width="60" alt="No Data" />
            <div style={{ marginTop: '5px', fontWeight: '500', fontSize: '25px', fontFamily: 'Noto Sans, sans-serif' }}>
              There are no recent Document to show.
            </div>
          </Grid>
        )}
      </div>
    </div>
  );
};

export default ViewFile;
